import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import ProductVisual from "@/components/ProductVisual";
import { getBundlePhoto } from "@/lib/productPhotos";

export default function BundleCard({ bundle }) {
  const { addItem } = useCart();
  const { format: fmtPrice } = useCurrency();
  const isLive = !bundle.coming_soon;
  const add = (e) => {
    // Prevent the outer <Link> wrapper from navigating when ADD is clicked,
    // ADD should drop the full stack into the cart flyout, nothing else.
    e.preventDefault();
    e.stopPropagation();
    addItem(
      { id: bundle.id, slug: bundle.slug, name: bundle.name, price: bundle.price, image: bundle.image },
      1
    );
  };

  // Live bundles route to /bundle/:slug detail page. Coming-soon bundles
  // stay non-navigable so users don't hit a dead-end detail page.
  const Wrapper = isLive ? Link : "div";
  const wrapperProps = isLive ? { to: `/bundle/${bundle.slug}` } : {};
  const photo = getBundlePhoto(bundle.slug);

  return (
    <Wrapper
      {...wrapperProps}
      className={`flex flex-col overflow-hidden rounded-xl panel-warm bg-white sm:flex-row ${isLive ? "transition-shadow hover:shadow-[0_20px_40px_-20px_rgba(30,58,138,0.18)]" : ""}`}
      data-testid={`bundle-card-${bundle.slug}`}
    >
      <div className="relative aspect-[3/2] w-full shrink-0 overflow-hidden bg-white sm:aspect-auto sm:w-72">
        {photo ? (
          <img
            src={photo}
            alt={bundle.name}
            className="h-full w-full object-contain object-center"
            // Bundle cards render below the /bundles hero and below the
            // homepage bundles strip, so they are safely lazy.
            loading="lazy"
            decoding="async"
            draggable={false}
            data-testid={`bundle-card-photo-${bundle.slug}`}
          />
        ) : (
          <ProductVisual name={bundle.name} format="bundle" />
        )}
        {bundle.coming_soon ? (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-navy px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.1em] text-white" data-testid={`bundle-coming-soon-${bundle.slug}`}>
            COMING SOON
          </span>
        ) : (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-brandblue px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.1em] text-white">
            SAVE {bundle.save_percent}%
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="label-mono">Multi-order savings</p>
        <h3 className="mt-1 font-heading text-xl font-bold text-ink">{bundle.name}</h3>
        <p className="mt-1 text-sm text-subtle">{bundle.short_description}</p>
        <ul className="mt-3 space-y-1">
          {bundle.items?.map((i) => (
            <li key={i} className="text-sm text-ink">• {i}</li>
          ))}
        </ul>
        {bundle.original_price && bundle.price && (
          <p
            className="mt-3 rounded-lg bg-brandbluelight px-3 py-2 font-mono text-[11px] tracking-[0.1em] text-brandbluedark"
            data-testid={`bundle-savings-${bundle.slug}`}
          >
            {fmtPrice(bundle.original_price)} bought separately · {fmtPrice(bundle.price)} bundled ·
            {" "}<strong>save {fmtPrice(bundle.original_price - bundle.price)}</strong>
          </p>
        )}
        <div className="mt-auto flex items-center justify-between pt-5">
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-2xl font-black text-ink">{fmtPrice(bundle.price)}</span>
            <span className="text-sm text-subtle line-through">{fmtPrice(bundle.original_price)}</span>
          </div>
          {bundle.coming_soon ? (
            <span
              className="btn-pill btn-outline px-6 py-2.5 font-mono text-[10px] font-bold tracking-[0.12em] text-subtle"
              data-testid={`bundle-coming-soon-label-${bundle.slug}`}
            >
              COMING SOON
            </span>
          ) : (
            <button
              onClick={add}
              className="btn-pill bg-navy px-6 py-2.5 text-xs font-bold tracking-wide text-white hover:bg-navydark"
              data-testid={`bundle-card-add-${bundle.slug}`}
            >
              ADD
            </button>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
